import { AuthService } from '@app/core/auth/auth.service';
import { SpinnerService } from '@app/core/spinner/spinner.service';
import { finalize } from 'rxjs/operators';
import { InfoService } from '@app/core/info/info.service';
import { Permission } from '@app/core/user/model/Permission.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionCV } from '@app/shared/model/cv/ActionCV.model';
import { TranslateService } from '@ngx-translate/core';
import { AbstractComponent } from './AbstractComponent';
import { EntityInterface } from '../model/entity/interfaces/entityInterface.model';
import { EntityServiceInterface } from '../service/interfaces/EntityServiceInterface';
import { Observable } from 'rxjs';
import { ConfirmationService } from '@app/core/confirmation/confirmation.service';
import { ConfirmationResponse } from '@app/core/confirmation/confirmationResponse.model';

export abstract class AbstractEntityComponent extends AbstractComponent {

  // ****************************** Propiedades **********************

  isLoading = false;

  // ****************************** CONSTRUCTOR ***************************

  constructor(
    public authService: AuthService,                           // servicio de autenticación
    public spinner: SpinnerService,                            // servicio de spinner
    public infoService: InfoService,                           // servicio de información
    public modalService: NgbModal,                             // servicio para ventanas modales
    public translate: TranslateService,
    public confirmationService?: ConfirmationService,
    private removeWithComment = false
  ) {
    super(authService, translate);
  }

  // ****************************** Implementar metodos abstractos *****


  // ****************************** Metodos de acciones estandard *****

  onFindById(entityName: string, entity: EntityInterface, showDetail = false, permission?: Permission, entityService?: EntityServiceInterface, module?: string): void {

    if (showDetail) {
      // entity.element = this.getElementMock();
      this.showDetail(entityName, entity, permission, entityService, module);
    }

  }

  public showDetail(
    entityName: string,
    entity: EntityInterface,
    permission?: Permission,
    entityService?: EntityServiceInterface,
    module?: string,
    action?: string,
    resultFunction?: (localEntityName: string, localEntity: EntityInterface) => void): void
  {

    const detailRef = this.modalService.open(this.getDetailComponentClass(entityName), { size: this.getDetailModalSize(entityName), backdrop: 'static' });
    // para hacerlo centrado
    // const detailRef = this.modalService.open(this.getDetailComponentClass(entityName), { centered: true, size: this.getDetailModalSize(entityName), backdrop: 'static' });
    const updatePermission = this.hasPermission(permission, 'W');
    detailRef.componentInstance.action = action ? action : (updatePermission ? ActionCV.all.UPDATE : ActionCV.all.DETAIL);
    detailRef.componentInstance.id = entityService.getURLIdFromEntity(entity);
    detailRef.componentInstance.entity = entity;
    detailRef.componentInstance.module = module;
    detailRef.componentInstance.entityName = entityName;

    detailRef.result
    .then((entityUpdate: EntityInterface) => {
      if (resultFunction) {
        resultFunction(entityName, entityUpdate);
      } else {
        // TO DO MODIFICAR EN PANTALLA
        this.onUpdate(entityName, entityUpdate);
      }
    })
    .catch(() => console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)'));

  }

  // -------------------------------------------------------------------------------
  // captura todos los entityos a partir de una url
  findByObserbable(entityName: string, obserbable: Observable<EntityInterface>): void {

    this.spinner.startSpinner(`entity.${entityName.charAt(0).toLowerCase() + entityName.slice(1)}.actions.loading`);

    // llamamos al servicio para capturar los entityos de la entidad
    obserbable
      .pipe(finalize(() => {
        this.spinner.stopSpinner();
      }))
      .subscribe({
        next: (entities: Array<EntityInterface>) => {
          this.onFindAll(entityName, entities);
        },
        error: () => {
          // LOG
          console.error(`[${entityName}] - findByObserbable - Service return error`);
          // LOG
        }
    });
  }

  // -------------------------------------------------------------------------------
  // captura todos los entityos de la entidad
  findAll(entityName: string, entityService: EntityServiceInterface, withSpinner: boolean = true, specialist?: string): void {

    this.isLoading = true;

    if (withSpinner) {
      this.spinner.startSpinner(`entity.${entityName.charAt(0).toLowerCase() + entityName.slice(1)}.actions.loading`);
    }

    // llamamos al servicio para capturar los entityos de la entidad
    entityService.findAll(true, specialist)
      .pipe(finalize(() => {
        if (withSpinner) {
          this.spinner.stopSpinner();
        }
      }))
      .subscribe({
        next: (entities: Array<EntityInterface>) => {
          this.onFindAll(entityName, entities);
          this.isLoading = false;
        },
        error: () => {
          // LOG
          this.isLoading = false;
          console.error(`[${entityName}] - getAll - Service return error`);
          // LOG
        }
      });
  }

  // -------------------------------------------------------------------------------
  // captura un entityo de la entidad
  findById(entityName: string, entityService: EntityServiceInterface, id: string, showModal = false, permission?: Permission, module?: string): void {

    this.spinner.startSpinner(`entity.${entityName.charAt(0).toLowerCase() + entityName.slice(1)}.actions.getting`);

    // llamamos al servicio para capturar los entityos de la entidad
    entityService.findById(id)
      .pipe(finalize(() => {
        this.spinner.stopSpinner();
      }))
      .subscribe({
        next: (entity: EntityInterface) => {
          this.onFindById(entityName, entity, showModal, permission, entityService, module);
        },
        error: () => {
          // LOG
          console.error(`[${entityName}] - get - Service return error`);
          // LOG
        }
      });
  }

  // -------------------------------------------------------------------------------
  // borra un entityo de la entidad
  public delete(entityName: string, entityService: EntityServiceInterface, id: string): void {
    if (this.confirmationService) {
      // pidiendo confirmación
      this.confirmationService.confirm(`common.confirmation.${ActionCV.all.REMOVE}`, null, this.removeWithComment)
      .then((confirmResponse: ConfirmationResponse) => {
        if (confirmResponse && confirmResponse.ok) {
          this.doDelete(entityName, entityService, id, this.removeWithComment, confirmResponse.comment);
        }
      })
      .catch(() => console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)'));
    } else {
      this.doDelete(entityName, entityService, id);
    }
  }



  // -------------------------------------------------------------------------------
  // para añadir un entityo
  openInsert(entityName: string, module: string, initDataToCreate?: EntityInterface): void {

    const detailRef = this.modalService.open(this.getDetailComponentClass(entityName), { centered: true, size: this.getDetailModalSize(entityName), backdrop: 'static' });
    detailRef.componentInstance.action = ActionCV.all.INSERT;
    detailRef.componentInstance.module = module;
    detailRef.componentInstance.entityName = entityName;
    detailRef.componentInstance.initDataToCreate = initDataToCreate;

    detailRef.result
    .then((entity: EntityInterface) => {
      this.onCreate(entityName, entity);
    })
    .catch(() => console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)'));

  }

  // -------------------------------------------------------------------------------
  // para abrir un detalle
  openDetail(entityName: string, module: string, id: string, permission: Permission, entityService: EntityServiceInterface): void {

    this.findById(entityName, entityService, id, true, permission, module);

  }

  // // -------------------------------------------------------------------------------
  // // para abrir un detalle
  // openHelpDetail(helpDetailEvent: any): void {

  //   if (helpDetailEvent.event === ActionCV.all.INSERT) {
  //     this.openInsert(
  //       helpDetailEvent.entityName,
  //       helpDetailEvent.module,
  //       helpDetailEvent.id);
  //   } else {
  //     this.findById(
  //       helpDetailEvent.entityName,
  //       helpDetailEvent.entityService,
  //       helpDetailEvent.id, true,
  //       helpDetailEvent.permission,
  //       helpDetailEvent.module);
  //   }

  // }


  // -------------------------------------------------------------------------------
  // borra un entityo de la entidad
  private doDelete(entityName: string, entityService: EntityServiceInterface, id: string, withComment = false, comment: string = null): void {

    this.spinner.startSpinner(`entity.${entityName.charAt(0).toLowerCase() + entityName.slice(1)}.actions.delete.deleting`);

    // llamamos al servicio para borrar el entityo
    entityService.delete(id, comment, withComment)
      .pipe(finalize(() => {
        this.spinner.stopSpinner();
      }))
      .subscribe({
        next: () => {

        //   // borra los entityos de la tabla
        //   this.tableRemove(event.rowIndex);
          // mustra la información con el borrado
          this.infoService.showInfo(`entity.${entityName.charAt(0).toLowerCase() + entityName.slice(1)}.actions.delete.ok`);
          // devuleve los objetos al hijo
          this.onDelete(entityName, id);
        },
        error: () => {
          // LOG
          console.error(`[${entityName}] - delete - Service return error`);
          // LOG
        }
      });
  }

  // ****************************** Abstractos *************************

  abstract getDetailComponentClass(entityName: string): any;
  abstract getDetailModalSize(entityName: string): 'sm' | 'lg' | 'xl';
  abstract onCreate(entityName: string, entity: EntityInterface): void;
  abstract onUpdate(entityName: string, entity: EntityInterface): void;
  abstract onDelete(entityName: string, id: string): void;
  abstract onFindAll(entityName: string, entities: Array<EntityInterface>): void;

}
